import React from 'react';
import { Grid, Typography, Paper, Skeleton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import UserAvatar from 'shared/components/UserAvatar';
import { completeFullDateFormat } from 'utilities/formatDate';
import { KFButton } from '@kf/ic2fx';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { IMakeDecisionData } from '.';
import { colors } from 'styles/colors';
import {
  HMDecisionModalActions,
  THMDecisionModalActions,
} from 'modules/hiring/models/hiringDecisionOption';
import { HiringActivity } from 'modules/hiring/models/candidatePipeline.model';

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  border: 'none',
}));

interface IMakeDecisionHeaderProps {
  headerData: IMakeDecisionData | null;
  selectedAction: (action: THMDecisionModalActions) => void;
  activeDecision?: THMDecisionModalActions;
  loading?: boolean;
  children?: React.ReactNode;
}

const shimmerCount = 3;

const LoadingSkeleton = () => {
  return (
    <>
      <Skeleton
        data-testid='skeleton-1'
        variant='rectangular'
        width={210}
        height={30}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
        }}
      />
      <Skeleton
        data-testid='skeleton-2'
        variant='rectangular'
        width={210}
        height={22}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
          mt: '0.25rem !important',
        }}
      />
      <Skeleton
        data-testid='skeleton-3'
        variant='rectangular'
        width={210}
        height={18}
        sx={{
          bgcolor: (theme) => theme.palette.neutral?.main,
          mt: '0.25rem !important',
        }}
      />
    </>
  );
};

const LoadingButtonSkeleton = () => {
  return (
    <>
      {[...Array(shimmerCount)]?.map((_, i) => {
        return (
          <Skeleton
            key={i}
            variant='rectangular'
            width={98}
            height={38}
            sx={{
              bgcolor: (theme) => theme.palette.neutral?.main,
            }}
          />
        );
      })}
    </>
  );
};

const MakeDecisionModalHeader = ({
  headerData,
  selectedAction,
  activeDecision,
  loading,
  children,
}: IMakeDecisionHeaderProps) => {
  const { t: translate } = useTranslation();
  const isButtonDisabled = () => {
    return !!activeDecision;
  };

  const disableStyles = (key: string) => {
    return key === activeDecision
      ? {
          background: colors.background.pearlGrey,
          border: 'none',
        }
      : {
          background: `${colors.background.componentBackground} !important`,
          border: `0.063rem solid ${colors.tertiary.darkGray} !important`,
        };
  };

  return (
    <Grid
      container
      spacing={2}
      data-testid='make-decision-modal-header'
      sx={{
        pb: '1.25rem',
      }}
    >
      <Grid item sx={{ width: '8.5rem' }}>
        {loading ? (
          <Skeleton
            variant='circular'
            data-testid='user-avatar-skeleton'
            width={96}
            height={96}
            sx={{ bgcolor: (theme) => theme.palette.neutral?.main }}
          />
        ) : (
          <UserAvatar
            className='kfic2-heroBold'
            data-testid='user-avatar'
            employeeFullName={headerData?.name ?? ''}
            avatarSxProps={{
              width: '6rem',
              height: '6rem',
              fontSize: '2.25rem !important',
            }}
          />
        )}
      </Grid>
      <Grid
        item
        sx={{
          pl: '0rem !important',
          pt: '0.5rem !important',
          width: 'calc(100% - 8.5rem)',
          mt: 'auto',
        }}
      >
        <Stack
          sx={{
            maxWidth: '50rem',
          }}
        >
          {!loading && (
            <Typography
              id='make-decision-candidate-name'
              data-testid='make-decision-candidate-name'
              className='kfic2-panelTitleBold'
              style={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                width: '98%',
                textAlign: 'left',
              }}
              title={headerData?.name}
            >
              {headerData?.name}
            </Typography>
          )}
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={7}>
            <Stack
              spacing={2}
              sx={{
                alignItems: 'start',
                mb: '0.5rem',
              }}
            >
              {loading ? (
                <Grid>
                  <LoadingSkeleton />
                </Grid>
              ) : (
                <>
                  <Item sx={{ mt: '0rem !important', p: 0 }}>
                    <Typography
                      id='feedback-modal-requesition-id'
                      data-testid='feedback-modal-requesition-id'
                      className='kfic2-bodyText'
                    >
                      {translate('forSomeSome', {
                        arg1: headerData?.jobTitle,
                        arg2: headerData?.requisitionId,
                        interpolation: { escapeValue: false },
                      })}
                    </Typography>
                  </Item>
                  <Item sx={{ mt: '0rem !important', p: 0 }}>
                    <Typography
                      id='feedback-modal-updated-date'
                      data-testid='feedback-modal-updated-date'
                      className='kfic2-metadata'
                    >
                      {translate('lastUpdated')}:
                      {` ${
                        headerData?.lastUpdatedDate
                          ? completeFullDateFormat(
                              headerData?.lastUpdatedDate,
                              'd MMMM yyyy'
                            )
                          : ''
                      }`}
                    </Typography>
                  </Item>
                </>
              )}
            </Stack>
          </Grid>
          <Grid item xs={5} sx={{ alignSelf: 'end' }}>
            <Stack spacing={2.5} direction='row' justifyContent='end'>
              {loading ? (
                <LoadingButtonSkeleton />
              ) : (
                <>
                  <KFButton
                    data-testid='pass-decision-button'
                    sx={{
                      '&.Mui-disabled': {
                        color: `${colors.tertiary.darkGray} !important`,
                        ...disableStyles('Pass'),
                      },
                    }}
                    disabled={isButtonDisabled()}
                    icon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
                    color='dangerTertiary'
                    onClick={() => selectedAction(HMDecisionModalActions.Pass)}
                  >
                    {translate('pass')}
                  </KFButton>
                  <KFButton
                    data-testid='offer-decision-button'
                    sx={{
                      '&.Mui-disabled': {
                        color: `${colors.tertiary.darkGray}!important`,
                        ...disableStyles(
                          headerData?.activityName ===
                            HiringActivity.InInterview
                            ? 'Offer'
                            : 'Advance'
                        ),
                      },
                    }}
                    disabled={isButtonDisabled()}
                    icon={
                      <CheckOutlinedIcon sx={{ fontSize: '1rem !important' }} />
                    }
                    color='quaternary'
                    onClick={() =>
                      headerData?.activityName === HiringActivity.InInterview
                        ? selectedAction(HMDecisionModalActions.Offer)
                        : selectedAction(HMDecisionModalActions.Advance)
                    }
                  >
                    {translate(
                      headerData?.activityName === HiringActivity.InInterview
                        ? 'offer'
                        : 'advance'
                    )}
                  </KFButton>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      {children}
    </Grid>
  );
};

export default MakeDecisionModalHeader;
